import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import type { CalendarEvent } from '../api/types';
import {
  formatDate,
  formatNumber,
  monthName,
  parseIsoDate,
  traditionLabel,
  useJourneyLocale,
  weekdayNamesShort,
} from '../i18n/journey';
import { useUser } from '../state/UserContext';

function formatDay(lang: string, dateStr: string): { day: number; weekday: string } {
  // Parse as a plain calendar date (no timezone shift) rather than via `new Date(dateStr)`,
  // which would interpret an ISO date-only string as UTC midnight and can roll to the wrong
  // local day near midnight.
  const dt = parseIsoDate(dateStr);
  return {
    day: dt.getDate(),
    weekday: formatDate(lang, dt, { weekday: 'short' }),
  };
}

function todayIsoDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** One event's detail, shared between the list view's cards and the grid view's side panel, so
 * the two views never drift into showing different information. The date-confidence line and
 * region/community/start time sit behind a disclosure (layout pass 2026-09-10): shown inline for
 * all 37 events they were most of the page's 10,000px height. "Estimated" and "varies by year"
 * stay visible in the meta line, so uncertainty is never hidden behind a click. */
function EventDetail({ e, isNext }: { e: CalendarEvent; isNext: boolean }) {
  const { t } = useJourneyLocale();
  return (
    <div className="calendar-row-info">
      <div className="calendar-row-title">
        {e.title}
        {isNext && <span className="calendar-next-pill">{t('calendar.nextUp')}</span>}
      </div>
      <div className="calendar-row-meta">
        <span className="calendar-tradition-pill">{traditionLabel(t, e.tradition)}</span>
        {e.movable && <span title={t('calendar.variesTitle')}> &middot; {t('calendar.varies')}</span>}
        {e.uncertainty === 'estimated' && <span> &middot; {t('calendar.estimated')}</span>}
      </div>
      <p className="calendar-row-description">{e.description}</p>
      <details className="calendar-more">
        <summary>{t('calendar.details')}</summary>
        <p className="calendar-row-about">
          {e.uncertainty === 'estimated' ? t('calendar.estimated') : t('calendar.confirmedFor', { year: e.date.slice(0, 4) })}
          {e.movable && ` · ${t('calendar.movable')}`}
          {e.community && ` · ${t('calendar.communityNote')}`}
        </p>
        {(e.region || e.community || e.startTime) && (
          <div className="calendar-row-richness">
            {e.region && (
              <p className="calendar-row-richness-item">
                <span className="calendar-row-richness-label">{t('calendar.region')}</span> {e.region}
              </p>
            )}
            {e.community && (
              <p className="calendar-row-richness-item">
                <span className="calendar-row-richness-label">{t('calendar.community')}</span> {e.community}
              </p>
            )}
            {e.startTime && (
              <p className="calendar-row-richness-item">
                <span className="calendar-row-richness-label">{t('calendar.start')}</span> {e.startTime}
              </p>
            )}
          </div>
        )}
      </details>
      {e.relatedPillarId && (
        <Link to={`/practices?tradition=${encodeURIComponent(e.tradition)}`} className="calendar-row-practice-link">
          {t('calendar.practiceWith', { tradition: traditionLabel(t, e.tradition) })} &rsaquo;
        </Link>
      )}
    </div>
  );
}

export function Calendar() {
  const { me } = useUser();
  const { t, lang } = useJourneyLocale();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [traditionFilter, setTraditionFilter] = useState<string>('all');
  // Months before the current one are collapsed by default: the list used to open in January.
  const [showEarlier, setShowEarlier] = useState(false);
  // {year, month} with month 0-indexed (JS Date convention) -- initialized once events load, to
  // today's month if it has any seeded data, else the first month that actually has an event.
  const [gridMonth, setGridMonth] = useState<{ year: number; month: number } | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getCalendar()
      .then((res) => {
        if (!cancelled) setEvents(res.events);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t('calendar.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `t` is read at failure time only; a language change must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const today = todayIsoDate();
  const currentMonthKey = today.slice(0, 7);
  const traditionsPresent = useMemo(() => Array.from(new Set(events.map((e) => e.tradition))), [events]);
  const sorted = useMemo(
    () =>
      events
        .filter((e) => traditionFilter === 'all' || e.tradition === traditionFilter)
        .sort((a, b) => a.date.localeCompare(b.date)),
    [events, traditionFilter],
  );
  const nextUpcomingId = useMemo(() => sorted.find((e) => e.date >= today)?.eventId, [sorted, today]);
  const weekdays = useMemo(() => weekdayNamesShort(lang), [lang]);

  // Group into month buckets ("2026-09" -> events), preserving chronological order.
  const monthGroups = useMemo(() => {
    const groups = new Map<string, CalendarEvent[]>();
    for (const e of sorted) {
      const key = e.date.slice(0, 7);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(e);
    }
    return Array.from(groups.entries());
  }, [sorted]);

  // With nothing upcoming (every listed date has passed) nothing is collapsed.
  const earlierGroups = nextUpcomingId ? monthGroups.filter(([key]) => key < currentMonthKey) : [];
  const earlierCount = earlierGroups.reduce((n, [, monthEvents]) => n + monthEvents.length, 0);
  const visibleGroups = showEarlier || earlierCount === 0 ? monthGroups : monthGroups.filter(([key]) => key >= currentMonthKey);

  // Every event keyed by its exact date, for O(1) grid-cell lookups.
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of sorted) {
      if (!map.has(e.date)) map.set(e.date, []);
      map.get(e.date)!.push(e);
    }
    return map;
  }, [sorted]);

  // Default the grid to today's month if it has any seeded data, else the first month that
  // actually has an event -- never an arbitrary empty month on first load.
  useEffect(() => {
    if (gridMonth || sorted.length === 0) return;
    const hasToday = sorted.some((e) => e.date.slice(0, 7) === currentMonthKey);
    const [y, m] = (hasToday ? currentMonthKey : sorted[0].date.slice(0, 7)).split('-').map(Number);
    setGridMonth({ year: y, month: m - 1 });
  }, [sorted, gridMonth, currentMonthKey]);

  function shiftGridMonth(delta: number) {
    setGridMonth((prev) => {
      if (!prev) return prev;
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
    setSelectedDate(null);
  }

  function chooseTradition(tradition: string) {
    setTraditionFilter(tradition);
    setSelectedDate(null);
  }

  // Standard 7-column month grid: leading/trailing blanks so the 1st lands under its real weekday.
  const gridCells = useMemo(() => {
    if (!gridMonth) return [];
    const { year, month } = gridMonth;
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: Array<{ date: string; day: number } | null> = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push({ date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`, day });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [gridMonth]);

  const gridMonthKey = gridMonth ? `${gridMonth.year}-${String(gridMonth.month + 1).padStart(2, '0')}` : '';
  const gridMonthEvents = sorted.filter((e) => e.date.startsWith(gridMonthKey));
  const selectedEvents = selectedDate ? eventsByDate.get(selectedDate) ?? [] : [];

  return (
    <div className="calendar-page">
      <h1 className="page-title">{t('calendar.title')}</h1>
      <p className="page-subtitle">{t('calendar.subtitle')}</p>

      {!loading && events.length > 0 && (
        <div className="calendar-toolbar">
          <div className="calendar-view-toggle" role="tablist" aria-label={t('calendar.viewAria')}>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'list'}
              className={`calendar-view-toggle-btn${view === 'list' ? ' active' : ''}`}
              onClick={() => setView('list')}
            >
              {t('calendar.list')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'grid'}
              className={`calendar-view-toggle-btn${view === 'grid' ? ' active' : ''}`}
              onClick={() => setView('grid')}
            >
              {t('calendar.grid')}
            </button>
          </div>

          {traditionsPresent.length > 1 && (
            <div className="filter-row calendar-filter" role="group" aria-label={t('calendar.filterAria')}>
              <button
                type="button"
                className={`filter-chip${traditionFilter === 'all' ? ' selected' : ''}`}
                aria-pressed={traditionFilter === 'all'}
                onClick={() => chooseTradition('all')}
              >
                {t('calendar.allTraditions')}
              </button>
              {traditionsPresent.map((tradition) => (
                <button
                  key={tradition}
                  type="button"
                  className={`filter-chip${traditionFilter === tradition ? ' selected' : ''}`}
                  aria-pressed={traditionFilter === tradition}
                  onClick={() => chooseTradition(tradition)}
                >
                  {traditionLabel(t, tradition)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <p className="error-text">{error}</p>}

      {!loading && sorted.length > 0 && !nextUpcomingId && (
        <p className="calendar-coverage-note">
          {t('calendar.allPassed', { year: sorted[sorted.length - 1].date.slice(0, 4) })}
        </p>
      )}

      {loading ? (
        <div className="center-page" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="empty-state">{t('calendar.empty')}</div>
      ) : view === 'list' ? (
        <div className="calendar-list">
          {earlierCount > 0 && (
            <button
              type="button"
              className="btn btn-ghost calendar-earlier-toggle"
              aria-expanded={showEarlier}
              onClick={() => setShowEarlier((v) => !v)}
            >
              {showEarlier ? t('calendar.hideEarlier') : t('calendar.showEarlier', { n: formatNumber(lang, earlierCount) })}
            </button>
          )}
          {visibleGroups.map(([monthKey, monthEvents]) => {
            const [, m] = monthKey.split('-').map(Number);
            return (
              <section key={monthKey} className="calendar-month">
                <h2 className="calendar-month-title">{monthName(lang, m - 1)}</h2>
                <div className="calendar-cards">
                  {monthEvents.map((e) => {
                    const { day, weekday } = formatDay(lang, e.date);
                    const isPast = e.date < today;
                    const isNext = e.eventId === nextUpcomingId;
                    return (
                      <article
                        key={e.eventId}
                        className={`calendar-row calendar-card${isPast ? ' past' : ''}${isNext ? ' next' : ''}`}
                      >
                        <div className="calendar-date-badge">
                          <span className="calendar-date-day">{day}</span>
                          <span className="calendar-date-weekday">{weekday}</span>
                        </div>
                        <EventDetail e={e} isNext={isNext} />
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        gridMonth && (
          <div className="calendar-grid-layout">
            <div className="calendar-grid-wrap">
              <div className="calendar-grid-nav">
                <button
                  type="button"
                  className="calendar-grid-nav-btn"
                  onClick={() => shiftGridMonth(-1)}
                  aria-label={t('calendar.previousMonth')}
                >
                  &larr;
                </button>
                <h2 className="calendar-month-title">
                  {formatDate(lang, new Date(gridMonth.year, gridMonth.month, 1), { month: 'long', year: 'numeric' })}
                </h2>
                <button
                  type="button"
                  className="calendar-grid-nav-btn"
                  onClick={() => shiftGridMonth(1)}
                  aria-label={t('calendar.nextMonth')}
                >
                  &rarr;
                </button>
              </div>

              <div className="calendar-grid-weekdays">
                {weekdays.map((w, i) => (
                  <div key={i} className="calendar-grid-weekday">{w}</div>
                ))}
              </div>

              <div className="calendar-grid">
                {gridCells.map((cell, i) => {
                  if (!cell) return <div key={`blank-${i}`} className="calendar-grid-cell empty" />;
                  const dayEvents = eventsByDate.get(cell.date) ?? [];
                  const isToday = cell.date === today;
                  const isSelected = cell.date === selectedDate;
                  return (
                    <button
                      type="button"
                      key={cell.date}
                      className={`calendar-grid-cell${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}${dayEvents.length ? ' has-events' : ''}`}
                      onClick={() => dayEvents.length > 0 && setSelectedDate(cell.date === selectedDate ? null : cell.date)}
                      disabled={dayEvents.length === 0}
                      aria-pressed={dayEvents.length > 0 ? isSelected : undefined}
                    >
                      <span className="calendar-grid-cell-day">{cell.day}</span>
                      {dayEvents.length > 0 && (
                        <span className="calendar-grid-cell-dots">
                          {dayEvents.slice(0, 3).map((e) => (
                            <span key={e.eventId} className="calendar-grid-cell-dot" title={e.title} />
                          ))}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Beside the grid on wide screens (below it on phones): the chosen day's observances,
                or the whole month's as a compact, clickable list. */}
            <aside className="calendar-grid-side" aria-live="polite">
              {selectedEvents.length > 0 ? (
                <>
                  <button type="button" className="pp-link-button calendar-side-back" onClick={() => setSelectedDate(null)}>
                    &larr; {t('calendar.backToMonth')}
                  </button>
                  <div className="calendar-grid-detail">
                    {selectedEvents.map((e) => (
                      <div key={e.eventId} className="calendar-row calendar-grid-detail-row">
                        <EventDetail e={e} isNext={e.eventId === nextUpcomingId} />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h3 className="calendar-side-heading">{t('calendar.thisMonth')}</h3>
                  {gridMonthEvents.length === 0 ? (
                    <p className="calendar-side-hint">{t('calendar.noneThisMonth')}</p>
                  ) : (
                    <>
                      <ul className="calendar-side-list">
                        {gridMonthEvents.map((e) => {
                          const { day, weekday } = formatDay(lang, e.date);
                          return (
                            <li key={e.eventId}>
                              <button
                                type="button"
                                className={`calendar-side-item${e.date < today ? ' past' : ''}`}
                                onClick={() => setSelectedDate(e.date)}
                              >
                                <span className="calendar-side-date">
                                  <span className="calendar-date-day">{day}</span>
                                  <span className="calendar-date-weekday">{weekday}</span>
                                </span>
                                <span className="calendar-side-text">
                                  <span className="calendar-side-title">{e.title}</span>
                                  <span className="calendar-tradition-pill">{traditionLabel(t, e.tradition)}</span>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                      <p className="calendar-side-hint">{t('calendar.pickDay')}</p>
                    </>
                  )}
                </>
              )}
            </aside>
          </div>
        )
      )}

      {!loading && sorted.length > 0 && (
        <p className="calendar-footnote">
          {me?.traditions?.length ? t('calendar.footnoteSelected') : t('calendar.footnoteAll')}
        </p>
      )}
    </div>
  );
}
